import { useCallback, useEffect, useRef, useState } from 'react';
import type { DayView, SectionName } from '../lib/day';
import { dayApi, section } from '../lib/day';
import { approvalsApi } from '../lib/approvals';
import { api, ApiError } from '../lib/api';
import { formatDate, formatLitres, formatRs } from '../lib/money';
import OwnerSheet from './OwnerSheet';
import Diesel from './Diesel';
import Misc from './Misc';
import type { CostCode } from '../lib/costcodes';

/**
 * The owner's Approvals screen.
 *
 * "One Approvals screen. No day navigation — it opens on the oldest day still
 * waiting, says so, and counts how many are queued behind it. Three rows —
 * labour, diesel, misc — each with a summary and a status. Tapping one opens
 * that section inline; the labour section shows only the timesheet, since pay
 * runs have their own tab."
 *
 * There is deliberately no ‹ Day before / Day after › here. The queue decides
 * what is on screen.
 *
 * It polls every 25 seconds and, when something changes underneath, shows a
 * prompt rather than moving the view on its own — the prototype reassigns the
 * date inside `render()`, so the screen jumps under the owner's finger.
 */

interface Reference {
  plants: { id: string; code: string; name: string; cost_code_id?: string | null }[];
  miscCategories: { id: string; name: string; requires_plant: number }[];
  costCodes: CostCode[];
}

interface Props {
  reference: Reference;
  today: string;
}

const POLL_MS = 25_000;

export default function Approvals({ reference, today }: Props) {
  const [date, setDate] = useState<string | null>(null);
  const [moreWaiting, setMoreWaiting] = useState(0);
  const [view, setView] = useState<DayView | null>(null);
  const [open, setOpen] = useState<SectionName | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [newsPrompt, setNewsPrompt] = useState(false);
  const seqRef = useRef(0);

  /** Pick the day off the queue. Only ever called when there isn't one on screen. */
  const pickDay = useCallback(async () => {
    try {
      const queue = await approvalsApi.next();
      setMoreWaiting(queue.moreWaiting);
      setDate(queue.workDate ?? today);
      setError('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the queue.');
    }
  }, [today]);

  const load = useCallback(async () => {
    if (!date) return;
    try {
      const [day, queue] = await Promise.all([dayApi.get(date), approvalsApi.next()]);
      setView(day);
      setMoreWaiting(queue.moreWaiting);
      setNewsPrompt(false);
      setError('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the day.');
    }
  }, [date]);

  useEffect(() => { void pickDay(); }, [pickDay]);
  useEffect(() => { void load(); }, [load]);

  // Poll the change feed, not the day: one indexed read with a cursor.
  useEffect(() => {
    if (!date) return;
    let live = true;
    const tick = async () => {
      try {
        const result = await api.changes(seqRef.current);
        if (!live) return;
        if (seqRef.current === 0) {
          seqRef.current = result.seq;
          return;
        }
        if (result.seq !== seqRef.current) {
          seqRef.current = result.seq;
          // Prompt. Never move the view on its own.
          if (result.changes.some((change) => change.scope === `day:${date}` || change.scope === 'workers')) {
            setNewsPrompt(true);
          }
        }
      } catch {
        /* a failed poll is not worth interrupting anybody over */
      }
    };
    void tick();
    const timer = setInterval(() => void tick(), POLL_MS);
    return () => { live = false; clearInterval(timer); };
  }, [date]);

  if (!view || !date) {
    return <div className="empty">{error || 'Loading…'}</div>;
  }

  const closed = view.day.closedAt !== null;
  const allApproved = (['labour', 'diesel', 'misc'] as const).every(
    (name) => section(view, name).status === 'approved',
  );

  async function act(work: () => Promise<unknown>) {
    setBusy(true);
    setError('');
    try {
      await work();
      await load();
    } catch (err) {
      // `changedUnderneath` names the workers who moved while the owner read
      // the screen, so the message can say who rather than just "try again".
      if (err instanceof ApiError && Array.isArray(err.detail?.changed)) {
        const names = (err.detail.changed as { workerName: string }[])
          .map((row) => row.workerName)
          .join(', ');
        setError(`The office changed ${names} while you were reading. Have another look.`);
      } else {
        setError(err instanceof ApiError ? err.message : 'That did not work.');
      }
      await load();
    } finally {
      setBusy(false);
    }
  }

  const onScreen = () =>
    view.entries
      .filter((entry) => entry.attendance !== null)
      .map((entry) => ({ workerId: entry.workerId, rowVersion: entry.rowVersion }));

  return (
    <>
      {newsPrompt && (
        <div className="banner b-info" style={{ justifyContent: 'space-between' }}>
          <span>The office has changed something on this day.</span>
          <button type="button" className="btn sm" onClick={() => void load()}>
            Show me
          </button>
        </div>
      )}

      {error && <div className="banner b-no">{error}</div>}

      <div className="card">
        <div className="row" style={{ paddingTop: 0 }}>
          <div>
            <h3>
              Approvals — {formatDate(date)}
              {date === today && ' · today'}
            </h3>
            <div className="sub">
              {date < today && 'The oldest day still waiting. '}
              Open each section, check it, approve it. The day is approved with the last one.
              {moreWaiting > 0 &&
                ` ${moreWaiting} more day${moreWaiting > 1 ? 's' : ''} waiting after this one.`}
            </div>
          </div>
          <div className="stat">
            <div className="name">{formatRs(view.totals.costRs)}</div>
            <div className="sub">total</div>
          </div>
        </div>

        {(['labour', 'diesel', 'misc'] as const).map((name) => {
          const row = section(view, name);
          const isOpen = open === name;
          return (
            <div key={name}>
              <div
                className="row tap"
                role="button"
                tabIndex={0}
                onClick={() => setOpen(isOpen ? null : name)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') setOpen(isOpen ? null : name);
                }}
              >
                <div style={{ flex: 1 }}>
                  <div className="name">{labelOf(name)}</div>
                  <div className="sub">{summaryOf(view, name)}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  {row.status === 'approved' ? (
                    <span className="pill p-ok">Approved</span>
                  ) : row.status === 'submitted' ? (
                    <span className="pill p-wait">Waiting for you</span>
                  ) : (
                    <span className="pill p-no">Clerk still working</span>
                  )}
                  <span className="sub" style={{ fontSize: 18, color: 'var(--text-3)' }}>
                    {isOpen ? '⌄' : '›'}
                  </span>
                </div>
              </div>

              {isOpen && (
                <div
                  style={{
                    background: 'var(--surface-2)', borderRadius: 12,
                    padding: '10px 12px', margin: '0 -4px 10px',
                  }}
                >
                  {row.status === 'open' && (
                    <div className="banner b-info" style={{ margin: '0 0 8px' }}>
                      The clerk has not submitted this section yet. You can look, but wait
                      before approving.
                    </div>
                  )}

                  {/* Labour shows only the timesheet here — pay runs have their
                      own tab. */}
                  {name === 'labour' && (
                    <OwnerSheet
                      view={view}
                      costCodes={reference.costCodes}
                      onChanged={() => void load()}
                      onError={setError}
                    />
                  )}
                  {name === 'diesel' && (
                    <Diesel
                      view={view}
                      plants={reference.plants}
                      costCodes={reference.costCodes}
                      isOwner
                      canSetRate
                      onChanged={() => void load()}
                      onError={setError}
                    />
                  )}
                  {name === 'misc' && (
                    <Misc
                      view={view}
                      plants={reference.plants}
                      categories={reference.miscCategories}
                      costCodes={reference.costCodes}
                      isOwner
                      onChanged={() => void load()}
                      onError={setError}
                    />
                  )}

                  {!closed && (
                    <div className="bar" style={{ marginTop: 10 }}>
                      {row.status === 'approved' ? (
                        <button
                          type="button"
                          className="btn"
                          disabled={busy}
                          onClick={() => void act(() => approvalsApi.undoSection(date, name))}
                        >
                          Undo approval
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="btn pri"
                          disabled={busy || row.status === 'open'}
                          onClick={() =>
                            void act(() => approvalsApi.approveSection(date, name, onScreen()))
                          }
                        >
                          {name === 'labour'
                            ? `Approve labour${pendingCount(view) ? ` (${pendingCount(view)} employees)` : ''}`
                            : `Approve ${labelOf(name).toLowerCase()}`}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {closed ? (
        <>
          <div className="banner b-ok">
            Day approved
            {view.day.closedAt && ` at ${new Date(view.day.closedAt).toLocaleString('en-GB')}`}. The
            clerk can start the next day.
          </div>
          <div className="bar">
            <button type="button" className="btn" disabled>
              Print approved report (A4) — step 6
            </button>
            <button
              type="button"
              className="btn dang"
              disabled={busy}
              onClick={() => void act(() => approvalsApi.reopenDay(date))}
            >
              Reopen day
            </button>
          </div>
        </>
      ) : allApproved ? (
        // Only after "Reopen day": approving the last section closes the day
        // by itself, so otherwise this is never reached.
        <button
          type="button"
          className="btn pri"
          style={{ width: '100%' }}
          disabled={busy}
          onClick={() => void act(() => approvalsApi.closeDay(date))}
        >
          Approve the day and let the clerk start tomorrow
        </button>
      ) : (
        <button type="button" className="btn" style={{ width: '100%' }} disabled>
          Approve all three sections — the day is approved with the last one
        </button>
      )}
    </>
  );
}

function labelOf(name: SectionName): string {
  return name === 'labour' ? 'Labour' : name === 'diesel' ? 'Diesel' : 'Misc expenses';
}

function pendingCount(view: DayView): number {
  return view.entries.filter(
    (entry) => entry.attendance === 'worked' && entry.status !== 'approved',
  ).length;
}

function summaryOf(view: DayView, name: SectionName): string {
  if (name === 'labour') {
    const worked = view.entries.filter((entry) => entry.attendance === 'worked').length;
    const pending = pendingCount(view);
    return (
      `${worked} worked · ${formatRs(view.totals.labourRs)}` +
      (pending ? ` · ${pending} not approved` : '')
    );
  }
  if (name === 'diesel') {
    return `${formatLitres(view.totals.litresX100)} L @ ${formatRs(view.day.dieselRateRs)} · ${formatRs(view.totals.dieselRs)}`;
  }
  return `${view.misc.length} items · ${formatRs(view.totals.miscRs)}`;
}
